import {isFeatureEnabled} from '@github-ui/feature-flags'
import {IssueAgentTasksProvider} from '@github-ui/issue-agent-tasks/contexts/IssueAgentTasksContext'
import {CachePreheatContextProvider} from '@github-ui/preheat-issues-manager/cache-preheat-context'
import type {IssueViewerFeed} from '@github-ui/preheat-issues-manager/issue-viewer-storage-key'
import {UserNameDisplayContextProvider} from '@github-ui/profile-reference/UserNameDisplayContext'
import {useIsDataRouterEnabled} from '@github-ui/react-core/future/use-is-data-router-enabled'
import {ArtifactsPanelProvider} from '@github-ui/scribe-artifacts/ArtifactPanelContext'
import {clsx} from 'clsx'
import {type ReactNode, useCallback} from 'react'

import {IssueViewerContextProvider} from '../contexts/IssueViewerContext'
import {ArtifactsPanelLayout} from './artifacts-section/ArtifactsPanelLayout'
import {
  IssueViewerCore,
  type IssueViewerCoreIssue,
  type IssueViewerCoreProps,
  type IssueViewerViewerFragment,
} from './IssueViewer'
import styles from './IssueViewer.module.css'
import {useSecondaryQuery} from './IssueViewerSecondaryView'
import type {OptionConfig} from './OptionConfig'
import {type IssueTimelineRenderProps, TanStackIssueTimeline} from './timeline/IssueTimeline'
import {useIssueViewerContainerQueryFix} from './use-issue-viewer-container-query-fix'
import {useIssueViewerNavigationBlocker, useIssueViewerNavigatorBlocker} from './use-issue-viewer-navigation-blocker'

export type DataRouterIssueViewerProps = {
  issue: IssueViewerCoreIssue
  feed: IssueViewerFeed
  /** The oldest timeline section selected alongside the issue. */
  initialBackAnchor?: string
  viewer: IssueViewerViewerFragment | null
  isRepoOwnerEnterpriseManaged?: boolean | null
  userNameDisplayConfiguration?: string | null
  /** The route's own page-query key, so live updates refresh the header and sidebar it renders. */
  routeQueryKey?: readonly unknown[]
  /** `?timeline_page=N`, for the crawler's isolated historical window. */
  timelinePage?: number
  /** Artifact number selected by the server-rendered issue artifact route. */
  linkedArtifactNumber?: number
  optionConfig: OptionConfig
}

/**
 * The issue viewer as the Data Router route renders it: the same component tree the Relay route
 * mounts, reading the page query's payload directly through the provider-free core. The preheat
 * context schedules TanStack tasks only.
 */
export function DataRouterIssueViewer({
  issue,
  feed,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  linkedArtifactNumber,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name
  const issueViewerContainerRef = useIssueViewerContainerQueryFix(owner, repo, issue.number)

  return (
    <div
      ref={issueViewerContainerRef}
      className={clsx(
        styles.issueViewerContainer,
        styles.mainContainer,
        isFeatureEnabled('issues_sticky_header') && styles.mainContainer_stickyHeader,
      )}
    >
      <CachePreheatContextProvider>
        <DataRouterIssueViewerContent
          issue={issue}
          feed={feed}
          initialBackAnchor={initialBackAnchor}
          viewer={viewer}
          isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
          userNameDisplayConfiguration={userNameDisplayConfiguration}
          routeQueryKey={routeQueryKey}
          timelinePage={timelinePage}
          linkedArtifactNumber={linkedArtifactNumber}
          optionConfig={optionConfig}
        />
      </CachePreheatContextProvider>
    </div>
  )
}

/** Split from the component above so `useSecondaryQuery` runs under the preheat context. */
function DataRouterIssueViewerContent({
  issue,
  feed,
  initialBackAnchor,
  viewer,
  isRepoOwnerEnterpriseManaged,
  userNameDisplayConfiguration,
  routeQueryKey,
  timelinePage,
  linkedArtifactNumber,
  optionConfig,
}: DataRouterIssueViewerProps) {
  const owner = issue.repository.owner.login
  const repo = issue.repository.name

  const [
    secondaryIssueData,
    hideClosedSubIssuesEnabled,
    initialNestedSubIssuesData,
    subIssueDeletionConnection,
    secondaryDataUpdatedAt,
  ] = useSecondaryQuery({
    owner,
    repo,
    number: issue.number,
  })
  return (
    <IssueAgentTasksProvider owner={owner} repo={repo} issueNumber={issue.number}>
      <UserNameDisplayContextProvider value={userNameDisplayConfiguration ?? undefined}>
        <ArtifactsPanelProvider>
          <DataRouterArtifactsLayout insideSidePanel={optionConfig.insideSidePanel}>
            <IssueViewerContextProvider>
              <DataRouterIssueViewerInternal
                issue={issue}
                feed={feed}
                initialBackAnchor={initialBackAnchor}
                viewer={viewer}
                isRepoOwnerEnterpriseManaged={isRepoOwnerEnterpriseManaged}
                secondaryIssueData={secondaryIssueData ?? undefined}
                initialNestedSubIssuesData={initialNestedSubIssuesData}
                initialNestedSubIssuesDataUpdatedAt={secondaryDataUpdatedAt}
                hideClosedSubIssuesEnabled={hideClosedSubIssuesEnabled}
                // Sub-issue clicks carry this to the host, so a child opened from this list can
                // delete itself out of the list it was opened from.
                subIssueDeletionConnection={subIssueDeletionConnection}
                timelinePage={timelinePage}
                linkedArtifactNumber={linkedArtifactNumber}
                issueUpdatesChannel={secondaryIssueData?.updatesChannel}
                routeQueryKey={routeQueryKey}
                optionConfig={optionConfig}
              />
            </IssueViewerContextProvider>
          </DataRouterArtifactsLayout>
        </ArtifactsPanelProvider>
      </UserNameDisplayContextProvider>
    </IssueAgentTasksProvider>
  )
}

export function DataRouterArtifactsLayout({
  children,
  insideSidePanel,
}: {
  children: ReactNode
  insideSidePanel?: boolean
}) {
  return <ArtifactsPanelLayout insideSidePanel={insideSidePanel}>{children}</ArtifactsPanelLayout>
}

type DataRouterIssueViewerInternalProps = Omit<
  IssueViewerCoreProps,
  'issue' | 'renderTimeline' | 'timelineAvailable'
> & {issue: IssueViewerCoreIssue}

function DataRouterIssueViewerInternal(props: DataRouterIssueViewerInternalProps) {
  const renderTimeline = useCallback(
    (timelineProps: IssueTimelineRenderProps) => (
      <TanStackIssueTimeline {...timelineProps} timelineData={props.issue} />
    ),
    [props.issue],
  )

  return (
    <>
      <IssueViewerNavigationBlocker />
      <IssueViewerCore {...props} renderTimeline={renderTimeline} timelineAvailable />
    </>
  )
}

export function IssueViewerNavigationBlocker() {
  const isDataRouterEnabled = useIsDataRouterEnabled()

  return isDataRouterEnabled ? <DataRouterIssueViewerNavigationBlocker /> : <NavigatorIssueViewerBlocker />
}

function DataRouterIssueViewerNavigationBlocker() {
  useIssueViewerNavigationBlocker()

  return null
}

function NavigatorIssueViewerBlocker() {
  useIssueViewerNavigatorBlocker()

  return null
}
